import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface BildirimKural {
    kuralID: number;
    kuralTipi: string;
    kuralAdi: string;
    aktifMi: boolean;
    gun1: number | null;
    gun2: number | null;
    haftaninGunleri: string | null;
    leadDurumIDler: string | null;
    ekKullanicilar: { kullaniciID: number; adSoyad: string }[];
}

interface Kullanici {
    kullaniciID: number;
    adSoyad: string;
}

interface LeadDurum {
    statusID: number;
    statusAdi: string;
}

const HAFTANIN_GUNLERI = [
    { no: 1, kisa: 'Pzt', tam: 'Pazartesi' },
    { no: 2, kisa: 'Sal', tam: 'Salı' },
    { no: 3, kisa: 'Çar', tam: 'Çarşamba' },
    { no: 4, kisa: 'Per', tam: 'Perşembe' },
    { no: 5, kisa: 'Cum', tam: 'Cuma' },
    { no: 6, kisa: 'Cmt', tam: 'Cumartesi' },
    { no: 7, kisa: 'Paz', tam: 'Pazar' },
];

const KURAL_ACIKLAMA: Record<string, { ikon: string; renk: string; aciklama: string }> = {
    VadeGecmisOdeme: { ikon: 'fa-exclamation-circle', renk: '#ef4444', aciklama: 'Vadesi geçmiş ve durumu Bekliyor olan ödemeler/tahsilatlar için vade tarihinden sonraki belirlenen günlerde bildirim gönderir.' },
    MusteriSunumu:   { ikon: 'fa-file-alt',           renk: '#8b5cf6', aciklama: 'Durumu Taslak veya Devam Ediyor olan müşteri sunumları için oluşturma tarihinden belirlenen gün sonra bildirim gönderir. Oluşturan kullanıcıya ve ek seçilen kullanıcılara gönderilir.' },
    EmlakSunumu:     { ikon: 'fa-home',               renk: '#0ea5e9', aciklama: 'Durumu Taslak veya Devam Ediyor olan emlak sunumları için oluşturma tarihinden belirlenen gün sonra bildirim gönderir. Oluşturan kullanıcıya ve ek seçilen kullanıcılara gönderilir.' },
    KiraSozlesmesi:  { ikon: 'fa-key',                renk: '#f59e0b', aciklama: 'Kira sözleşmesi bitiş tarihine belirlenen gün kala seçilen kullanıcılara bildirim gönderir.' },
    GecikmisArama:   { ikon: 'fa-phone-slash',        renk: '#f97316', aciklama: 'Gecikmeli arama planı olan potansiyel müşteriler için seçilen günlerde ilgili kullanıcıya ve ek kullanıcılara bildirim gönderir.' },
    BekleyenLead:    { ikon: 'fa-user-clock',         renk: '#6366f1', aciklama: 'Seçilen durumlardan birindeki potansiyel müşteriler için seçilen günlerde takipçi kullanıcıya ve ek kullanıcılara bildirim gönderir.' },
};

const parseGunler = (str: string | null): number[] => {
    if (!str) return [];
    return str.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
};

const parseLeadIDler = (str: string | null): number[] => {
    if (!str) return [];
    return str.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
};

const OtomatikBildirimKurallari: React.FC = () => {
    const { t } = useTranslation();
    const [kurallar, setKurallar] = useState<BildirimKural[]>([]);
    const [kullanicilar, setKullanicilar] = useState<Kullanici[]>([]);
    const [leadDurumlari, setLeadDurumlari] = useState<LeadDurum[]>([]);
    const [loading, setLoading] = useState(true);
    const [kaydedilen, setKaydedilen] = useState<Record<number, boolean>>({});
    const [testYapiliyor, setTestYapiliyor] = useState(false);
    const [testSonuc, setTestSonuc] = useState<{ gonderilenSayisi: number; mesajlar: string[]; basarili: boolean } | null>(null);

    // Form state — her kural için ayrı
    const [formlar, setFormlar] = useState<Record<number, {
        aktifMi: boolean;
        gun1: string;
        gun2: string;
        secilenGunler: number[];
        ekKullaniciIDler: number[];
        leadDurumIDler: number[];
    }>>({});

    useEffect(() => {
        Promise.all([
            axios.get('/api/BildirimKurallari'),
            axios.get('/api/Kullanicilar'),
            axios.get('/api/BildirimKurallari/lead-durumlari'),
        ]).then(([kurRes, kulRes, ldRes]) => {
            const k: BildirimKural[] = kurRes.data || [];
            setKurallar(k);
            setKullanicilar(kulRes.data || []);
            setLeadDurumlari(ldRes.data || []);

            const f: typeof formlar = {};
            k.forEach(kural => {
                f[kural.kuralID] = {
                    aktifMi: kural.aktifMi,
                    gun1: kural.gun1?.toString() ?? '',
                    gun2: kural.gun2?.toString() ?? '',
                    secilenGunler: parseGunler(kural.haftaninGunleri),
                    ekKullaniciIDler: kural.ekKullanicilar.map(u => u.kullaniciID),
                    leadDurumIDler: parseLeadIDler(kural.leadDurumIDler),
                };
            });
            setFormlar(f);
        }).catch(() => {
            toast.error(t('msg.islemHatasi'));
        }).finally(() => setLoading(false));
    }, []);

    const updateForm = (kuralID: number, patch: Partial<typeof formlar[0]>) => {
        setFormlar(prev => ({ ...prev, [kuralID]: { ...prev[kuralID], ...patch } }));
    };

    const toggleGun = (kuralID: number, gun: number) => {
        const mevcut = formlar[kuralID]?.secilenGunler ?? [];
        const yeni = mevcut.includes(gun) ? mevcut.filter(g => g !== gun) : [...mevcut, gun];
        updateForm(kuralID, { secilenGunler: yeni });
    };

    const toggleKullanici = (kuralID: number, kid: number) => {
        const mevcut = formlar[kuralID]?.ekKullaniciIDler ?? [];
        const yeni = mevcut.includes(kid) ? mevcut.filter(k => k !== kid) : [...mevcut, kid];
        updateForm(kuralID, { ekKullaniciIDler: yeni });
    };

    const toggleLeadDurum = (kuralID: number, did: number) => {
        const mevcut = formlar[kuralID]?.leadDurumIDler ?? [];
        const yeni = mevcut.includes(did) ? mevcut.filter(d => d !== did) : [...mevcut, did];
        updateForm(kuralID, { leadDurumIDler: yeni });
    };

    const handleKaydet = async (kural: BildirimKural) => {
        const f = formlar[kural.kuralID];
        if (!f) return;

        const gun1 = parseInt(f.gun1) || null;
        const gun2 = parseInt(f.gun2) || null;

        if (['VadeGecmisOdeme', 'MusteriSunumu', 'EmlakSunumu', 'KiraSozlesmesi'].includes(kural.kuralTipi)) {
            if (!gun1 || gun1 < 1) { toast.warning(t('msg.uyari')); return; }
        }
        if (['GecikmisArama', 'BekleyenLead'].includes(kural.kuralTipi)) {
            if (f.secilenGunler.length === 0) { toast.warning(t('msg.uyari')); return; }
        }

        setKaydedilen(prev => ({ ...prev, [kural.kuralID]: true }));
        try {
            await axios.put(`/api/BildirimKurallari/${kural.kuralID}`, {
                aktifMi: f.aktifMi,
                gun1,
                gun2: gun2 && gun2 !== gun1 ? gun2 : null,
                haftaninGunleri: f.secilenGunler.sort().join(',') || null,
                leadDurumIDler: f.leadDurumIDler.join(',') || null,
                ekKullaniciIDler: f.ekKullaniciIDler,
            });
            toast.success(t('msg.islemBasarili'));
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setKaydedilen(prev => ({ ...prev, [kural.kuralID]: false }));
        }
    };

    const handleTest = async () => {
        setTestYapiliyor(true);
        setTestSonuc(null);
        try {
            const res = await axios.post('/api/BildirimKurallari/test-gonder');
            setTestSonuc(res.data);
            if (res.data.gonderilenSayisi > 0) {
                toast.success(t('msg.islemBasarili'));
            } else {
                toast.info(t('msg.onayGerekli'));
            }
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setTestYapiliyor(false);
        }
    };

    if (loading) return (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i>
        </div>
    );

    return (
        <div>
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280', lineHeight: 1.6, flex: 1 }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 6, color: '#3b82f6' }}></i>
                    Bildirimler her gün <strong>sabah 08:00'de</strong> otomatik olarak gönderilir.
                    Kuralı aktif etmek için <strong>{t('durum.aktif')}</strong> anahtarını açın ve ayarları kaydedin.
                </p>
                <button
                    onClick={handleTest}
                    disabled={testYapiliyor}
                    style={{
                        background: testYapiliyor ? '#e5e7eb' : 'linear-gradient(135deg, #1a2440, #2d3f6b)',
                        color: testYapiliyor ? '#9ca3af' : '#fff',
                        border: 'none', borderRadius: 8,
                        padding: '9px 20px', cursor: testYapiliyor ? 'not-allowed' : 'pointer',
                        fontWeight: 600, fontSize: 13, flexShrink: 0,
                        display: 'flex', alignItems: 'center', gap: 8,
                        boxShadow: testYapiliyor ? 'none' : '0 2px 8px rgba(26,36,64,0.25)',
                        transition: 'all 0.15s',
                    }}
                >
                    {testYapiliyor
                        ? <><i className="fas fa-spinner fa-spin"></i> Çalışıyor...</>
                        : <><i className="fas fa-play-circle"></i> Şimdi Çalıştır (Test)</>}
                </button>
            </div>

            {/* Test sonucu */}
            {testSonuc && (
                <div style={{
                    border: `1.5px solid ${testSonuc.basarili ? '#bbf7d0' : '#fecaca'}`,
                    borderRadius: 10, padding: '14px 18px', marginBottom: 20,
                    background: testSonuc.basarili ? '#f0fdf4' : '#fef2f2',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <i className={`fas ${testSonuc.basarili ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
                            style={{ color: testSonuc.basarili ? '#16a34a' : '#dc2626', fontSize: 16 }}></i>
                        <span style={{ fontWeight: 700, fontSize: 14, color: testSonuc.basarili ? '#15803d' : '#b91c1c' }}>
                            Test Sonucu — {testSonuc.gonderilenSayisi} bildirim gönderildi
                        </span>
                        <button onClick={() => setTestSonuc(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14 }}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {testSonuc.mesajlar.map((m, i) => (
                            <div key={i} style={{ fontSize: 12, color: m.startsWith('✓') ? '#15803d' : '#b91c1c', fontFamily: 'monospace' }}>
                                {m}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {kurallar.length === 0 && (
                <div style={{
                    border: '1.5px dashed #fbbf24', borderRadius: 12, padding: '28px 24px',
                    background: '#fffbeb', display: 'flex', alignItems: 'flex-start', gap: 14,
                }}>
                    <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b', fontSize: 20, marginTop: 2 }}></i>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 4 }}>
                            Veritabanı tabloları oluşturulmamış
                        </div>
                        <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6 }}>
                            Önce <strong>bildirim_kurallari.sql</strong> dosyasını veritabanında çalıştırın,
                            ardından API'yi yeniden başlatın. SQL dosyası:
                            <code style={{ background: '#fef3c7', borderRadius: 4, padding: '1px 6px', margin: '0 4px', fontSize: 12 }}>
                                YapiCRM.API/SQL/bildirim_kurallari.sql
                            </code>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {kurallar.map(kural => {
                    const f = formlar[kural.kuralID];
                    if (!f) return null;
                    const meta = KURAL_ACIKLAMA[kural.kuralTipi] || { ikon: 'fa-bell', renk: '#6b7280', aciklama: '' };
                    const gunTabanli = ['GecikmisArama', 'BekleyenLead'].includes(kural.kuralTipi);
                    const offsetTabanli = ['VadeGecmisOdeme', 'MusteriSunumu', 'EmlakSunumu', 'KiraSozlesmesi'].includes(kural.kuralTipi);
                    const oncesiSonrasi = kural.kuralTipi === 'KiraSozlesmesi' ? 'gün önce' : 'gün sonra';

                    return (
                        <div key={kural.kuralID} style={{
                            border: `1px solid ${f.aktifMi ? meta.renk + '40' : '#e5e7eb'}`,
                            borderRadius: 12,
                            background: '#fff',
                            overflow: 'hidden',
                            boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
                        }}>
                            {/* Kural başlık */}
                            <div style={{
                                padding: '14px 20px',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex', alignItems: 'center', gap: 14,
                                background: f.aktifMi ? meta.renk + '08' : '#fafafa',
                            }}>
                                <div style={{
                                    width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                                    background: f.aktifMi ? meta.renk + '18' : '#f3f4f6',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className={`fas ${meta.ikon}`} style={{ color: f.aktifMi ? meta.renk : '#9ca3af', fontSize: 16 }}></i>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a2440' }}>{kural.kuralAdi}</div>
                                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{meta.aciklama}</div>
                                </div>
                                {/* Aktif toggle */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: f.aktifMi ? meta.renk : '#9ca3af' }}>
                                        {f.aktifMi ? 'Aktif' : 'Pasif'}
                                    </span>
                                    <div
                                        onClick={() => updateForm(kural.kuralID, { aktifMi: !f.aktifMi })}
                                        style={{
                                            width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
                                            background: f.aktifMi ? meta.renk : '#d1d5db',
                                            position: 'relative', transition: 'background 0.2s',
                                        }}
                                    >
                                        <div style={{
                                            position: 'absolute', top: 3, left: f.aktifMi ? 23 : 3,
                                            width: 18, height: 18, borderRadius: '50%',
                                            background: '#fff', transition: 'left 0.2s',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                        }}></div>
                                    </div>
                                </label>
                            </div>

                            {/* Kural içerik */}
                            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                                {/* Gün offset (kural 1-4) */}
                                {offsetTabanli && (
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                                            {kural.kuralTipi === 'KiraSozlesmesi'
                                                ? 'Bitiş tarihinden kaç gün önce bildirim gönderilsin?'
                                                : 'Kaç gün sonra bildirim gönderilsin?'}
                                        </div>
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                            {[{ field: 'gun1', label: '1. Bildirim' }, { field: 'gun2', label: '2. Bildirim' }].map(({ field, label }) => (
                                                <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <label style={{ fontSize: 12, color: '#6b7280', minWidth: 80 }}>{label}</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="365"
                                                        value={f[field as 'gun1' | 'gun2']}
                                                        onChange={e => updateForm(kural.kuralID, { [field]: e.target.value } as any)}
                                                        style={{
                                                            width: 80, border: '1.5px solid #e5e7eb', borderRadius: 7,
                                                            padding: '6px 10px', fontSize: 13, textAlign: 'center', color: '#1a2440',
                                                            outline: 'none',
                                                        }}
                                                        onFocus={e => (e.target.style.borderColor = meta.renk)}
                                                        onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
                                                    />
                                                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{oncesiSonrasi}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Haftanın günleri (kural 5-6) */}
                                {gunTabanli && (
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                                            Hangi günlerde bildirim gönderilsin?
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {HAFTANIN_GUNLERI.map(g => {
                                                const secili = f.secilenGunler.includes(g.no);
                                                return (
                                                    <button
                                                        key={g.no}
                                                        onClick={() => toggleGun(kural.kuralID, g.no)}
                                                        title={g.tam}
                                                        style={{
                                                            padding: '6px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                                                            cursor: 'pointer', transition: 'all 0.15s',
                                                            border: `1.5px solid ${secili ? meta.renk : '#e5e7eb'}`,
                                                            background: secili ? meta.renk + '15' : '#fff',
                                                            color: secili ? meta.renk : '#6b7280',
                                                        }}
                                                    >
                                                        {g.kisa}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Lead durumları (kural 6) */}
                                {kural.kuralTipi === 'BekleyenLead' && leadDurumlari.length > 0 && (
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                                            Hangi lead durumları için bildirim gönderilsin?
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {leadDurumlari.map(d => {
                                                const secili = f.leadDurumIDler.includes(d.statusID);
                                                return (
                                                    <button
                                                        key={d.statusID}
                                                        onClick={() => toggleLeadDurum(kural.kuralID, d.statusID)}
                                                        style={{
                                                            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                                            cursor: 'pointer', transition: 'all 0.15s',
                                                            border: `1.5px solid ${secili ? meta.renk : '#e5e7eb'}`,
                                                            background: secili ? meta.renk + '15' : '#fff',
                                                            color: secili ? meta.renk : '#6b7280',
                                                        }}
                                                    >
                                                        {secili && <i className="fas fa-check" style={{ marginRight: 5, fontSize: 10 }}></i>}
                                                        {d.statusAdi}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Ek kullanıcılar */}
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                                        {['MusteriSunumu', 'EmlakSunumu'].includes(kural.kuralTipi)
                                            ? 'Oluşturan kişiye ek olarak bildirim gönderilecek kullanıcılar'
                                            : ['GecikmisArama', 'BekleyenLead'].includes(kural.kuralTipi)
                                            ? 'İlgili kullanıcıya ek olarak bildirim gönderilecek kullanıcılar'
                                            : 'Bildirim gönderilecek kullanıcılar'}
                                    </div>
                                    <div style={{
                                        border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden',
                                        maxHeight: 180, overflowY: 'auto',
                                    }}>
                                        {kullanicilar.length === 0 ? (
                                            <div style={{ padding: 12, color: '#9ca3af', fontSize: 12 }}>Kullanıcı yok</div>
                                        ) : kullanicilar.map((k, idx) => {
                                            const secili = f.ekKullaniciIDler.includes(k.kullaniciID);
                                            return (
                                                <label
                                                    key={k.kullaniciID}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '8px 14px', cursor: 'pointer',
                                                        background: secili ? meta.renk + '08' : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                                                        borderBottom: idx < kullanicilar.length - 1 ? '1px solid #f5f5f5' : 'none',
                                                        transition: 'background 0.1s',
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={secili}
                                                        onChange={() => toggleKullanici(kural.kuralID, k.kullaniciID)}
                                                        style={{ width: 15, height: 15, accentColor: meta.renk, cursor: 'pointer' }}
                                                    />
                                                    <span style={{ fontSize: 13, color: secili ? '#1a2440' : '#374151', fontWeight: secili ? 600 : 400 }}>
                                                        {k.adSoyad}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {f.ekKullaniciIDler.length > 0 && (
                                        <div style={{ marginTop: 5, fontSize: 11, color: meta.renk, fontWeight: 600 }}>
                                            {f.ekKullaniciIDler.length} kullanıcı seçili
                                        </div>
                                    )}
                                </div>

                                {/* Kaydet butonu */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                                    <button
                                        onClick={() => handleKaydet(kural)}
                                        disabled={kaydedilen[kural.kuralID]}
                                        style={{
                                            background: f.aktifMi ? meta.renk : '#6b7280',
                                            color: '#fff', border: 'none', borderRadius: 8,
                                            padding: '8px 22px', cursor: kaydedilen[kural.kuralID] ? 'not-allowed' : 'pointer',
                                            fontWeight: 600, fontSize: 13,
                                            opacity: kaydedilen[kural.kuralID] ? 0.7 : 1,
                                            display: 'flex', alignItems: 'center', gap: 7,
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        {kaydedilen[kural.kuralID]
                                            ? <><i className="fas fa-spinner fa-spin"></i> Kaydediliyor...</>
                                            : <><i className="fas fa-save"></i> Kaydet</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default OtomatikBildirimKurallari;
